import { Component, ElementRef, Input, OnInit, QueryList, ViewChildren } from '@angular/core'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { Menu, MenuItem, Consumable, MenuCourse } from '../domain/Consumable'
import { ProductLibrary } from '../domain/ProductLibrary'
import { Venue } from '../domain/Venue'
import { ModifierGroup } from '../domain/Modifier'
import { courseLimitsDisplay } from './course-limits-display'
import { BookingDocket } from '../domain/BookingDocket'

@Component({
    selector: 'app-menu-view',
    templateUrl: './menu-view.component.html',
    styleUrls: ['./menu-workspace.sass', './menu-view.component.sass'],
})
export class MenuViewComponent implements OnInit {
    @Input() venue!: Venue
    @Input() menu!: Menu
    @Input() productLibrary!: ProductLibrary
    @Input() docket: BookingDocket | null = null
    @ViewChildren('courseSection', { read: ElementRef }) courseSections!: QueryList<ElementRef<HTMLElement>>

    courses!: {
        course: MenuCourse,
        items: {
            menuItem: MenuItem,
            consumable: Consumable,
            descriptionFull: string | null,
            descriptionShort: string | null,
            canTruncate: boolean,
            expanded: boolean,
            modifierGroups: Array<{
                id: string,
                name: string,
                modifiers: Array<{
                    name: string,
                    description: string | null
                }>
            }>
        }[]
    }[]
    characterLimit = 140

    constructor(
        public activeModal: NgbActiveModal
    ) {}

    ngOnInit() {
        this.courses = this.menu.menuCourses.map(course => ({
            course,
            items: course.menuItems.flatMap(menuItem => {
                const consumable = this.productLibrary.getConsumable(menuItem.consumableId)
                if (!consumable?.getSKUWithId(menuItem.skuId)?.onSale) {
                    return []
                }
                const canTruncate = !!consumable.description && consumable.description.length > this.characterLimit
                return {
                    menuItem: menuItem,
                    consumable,
                    descriptionFull: consumable.description,
                    descriptionShort: consumable.description ? consumable.description.slice(0, this.characterLimit) : null,
                    canTruncate,
                    expanded: false,
                    modifierGroups: consumable.modifierGroupIds
                        .map(id => this.productLibrary.getModifierGroup(id))
                        .filter((g): g is ModifierGroup => !!g)
                        .map(modifierGroup => ({
                            id: modifierGroup.id,
                            name: modifierGroup.name,
                            modifiers: modifierGroup.modifiers.map(modifier => ({
                                name: modifier.displayName,
                                description: modifier.description
                            }))
                        }))
                }
            })
        }))
    }

    dismiss() {
        this.activeModal.dismiss()
    }

    get menuCourseLimitsDisplay(): string | null {
        const limits = this.docket?.getCourseLimits(this.menu) ?? this.menu
        return courseLimitsDisplay(limits.minimumCoursesRequired, limits.maximumCoursesRequired)
    }

    getCourseLimitsDisplay(course: MenuCourse): string | null {
        const min = course.minimumItemsRequired
        const max = course.maximumItemsRequired
        
        if (min !== null && max !== null) {
            if (min === max) {
                return `Select exactly ${min} ${min === 1 ? 'item' : 'items'}`
            }
            return `Select ${min}-${max} items`
        } else if (min !== null) {
            return `Select at least ${min} ${min === 1 ? 'item' : 'items'}`
        } else if (max !== null) {
            return `Select up to ${max} ${max === 1 ? 'item' : 'items'}`
        }
        return null
    }

    toggleDescription(item: any) {
        item.expanded = !item.expanded
    }

    scrollToCourse(courseId: string) {
        const section = this.courseSections.find(ref => ref.nativeElement.dataset['courseId'] === courseId)?.nativeElement
        section?.scrollIntoView({ block: 'start' })
        section?.focus({ preventScroll: true })
    }
}
